import type { EnrichedPatternDisplay, PatternSearchResult } from '@mrb91002/shared-types'
import React, { useEffect, useState, useCallback } from 'react'

import { fetchNewestPatternsFull, searchPatternsWithFilters } from '../../../api/fetchers'
import { useAuth } from '../../../context/AuthContext'

import { HomeBanner, PatternCards } from './components'

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'most-reviewed', label: 'Most Reviewed' },
  { value: 'highest-rated', label: 'Highest Rated' },
] as const

type SortByValue = (typeof SORT_OPTIONS)[number]['value']

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
] as const

type DateRangeValue = (typeof DATE_RANGE_OPTIONS)[number]['value']

export const Home = () => {
  const { isLoading, session, idToken } = useAuth()
  const [patterns, setPatterns] = useState<(EnrichedPatternDisplay | PatternSearchResult)[]>([])
  const [lastKey, setLastKey] = useState<string | Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortByValue>(SORT_OPTIONS[0].value)
  const [dateRange, setDateRange] = useState<DateRangeValue>(DATE_RANGE_OPTIONS[0].value)
  const [hasMore, setHasMore] = useState(true)
  const [from, setFrom] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)

  // Page load logging - runs once on mount
  useEffect(() => {
    console.log('📄 [PAGE] Home page loaded')
  }, [])

  useEffect(() => {
    setPatterns([])
    setLastKey(null)
    setFrom(0)
    setFetchError(null)
    setHasMore(true)
    setHasSearched(false)
    setLoading(true) // Show loading when filters change
    loadPatterns(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, dateRange])

  const loadPatterns = useCallback(
    async (reset: boolean = false): Promise<void> => {
      try {
        if (reset) {
          setLoading(true)
        } else {
          setIsLoadingMore(true)
        }
        setFetchError(null)

        // Use DynamoDB for "newest" with "all" time, OpenSearch for everything else
        if (sortBy === 'newest' && dateRange === 'all') {
          const keyToUse = reset ? null : lastKey
          const result = await fetchNewestPatternsFull(15, keyToUse)
          const newItems = result.items || []
          const nextKey = result.lastKey || null

          if (reset) {
            setPatterns(newItems)
          } else {
            setPatterns((prev) => [...prev, ...newItems])
          }
          setLastKey(nextKey)
          setHasMore(!!nextKey)
        } else {
          // Use OpenSearch for all other combinations (including newest with date filters)
          const fromValue = reset ? 0 : from
          const result = await searchPatternsWithFilters(
            sortBy as SortByValue,
            dateRange as DateRangeValue,
            15,
            fromValue,
          )
          const newItems = (result.items || []).filter((item): item is PatternSearchResult => item !== undefined)

          if (reset) {
            setPatterns(newItems)
            setFrom(newItems.length)
          } else {
            setPatterns((prev) => [...prev, ...newItems])
            setFrom((prev) => prev + newItems.length)
          }
          setHasMore(fromValue + newItems.length < result.total)
        }
        setHasSearched(true)
      } catch (error: unknown) {
        setFetchError(error instanceof Error ? error.message : 'Error fetching display data')
        console.error('Error fetching display data:', error)
      } finally {
        setLoading(false)
        setIsLoadingMore(false)
      }
    },
    [lastKey, sortBy, dateRange, from],
  )

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSortBy = e.target.value as SortByValue
    setSortBy(newSortBy)
    // Reset date range to "all" when switching to "newest"
    if (newSortBy === 'newest') {
      setDateRange('all')
    }
  }

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateRange(e.target.value as DateRangeValue)
  }

  if (isLoading && patterns.length === 0 && !hasSearched) return <p>Loading...</p>

  console.log('[HOME] session', session)
  console.log('[HOME] idToken', idToken)
  console.log('[HOME] hostname:', window.location.hostname)

  return (
    <div
      style={{
        background: 'var(--cream-bg)',
        minHeight: '100vh',
        minWidth: 0,
        padding: 0,
        margin: 0,
      }}
    >
      <HomeBanner message />

      {/* <ExploreByCraft /> */}

      <main
        style={{
          padding: '80px 75px',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '40px 0 32px 0',
          }}
        >
          <h2
            style={{
              fontWeight: 400,
              fontSize: '2.25rem',
              fontFamily: 'var(--font-heading)',
              margin: 0,
              letterSpacing: '-0.01em',
              color: 'var(--dark-brown)',
            }}
          >
            Newest Patterns
          </h2>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                htmlFor="sort-select"
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'var(--medium-brown)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                Sort:
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={handleSortChange}
                style={{
                  borderRadius: 9999,
                  padding: '8px 16px',
                  fontSize: 16,
                  border: '1px solid var(--border-gray)',
                  background: 'var(--white)',
                  fontWeight: 400,
                  fontFamily: 'var(--font-body)',
                  color: 'var(--medium-brown)',
                  cursor: 'pointer',
                }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                htmlFor="date-range-select"
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'var(--medium-brown)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                From:
              </label>
              <select
                id="date-range-select"
                value={dateRange}
                onChange={handleDateRangeChange}
                disabled={sortBy === 'newest'}
                style={{
                  borderRadius: 9999,
                  padding: '8px 16px',
                  fontSize: 16,
                  border: '1px solid var(--border-gray)',
                  background: sortBy === 'newest' ? 'var(--light-gray)' : 'var(--white)',
                  fontWeight: 400,
                  fontFamily: 'var(--font-body)',
                  color: sortBy === 'newest' ? 'var(--text-gray)' : 'var(--medium-brown)',
                  cursor: sortBy === 'newest' ? 'not-allowed' : 'pointer',
                }}
              >
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '64px', color: '#64748b', fontSize: 16 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: '3px solid #e2e8f0',
                  borderTop: '3px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block',
                  marginBottom: 16,
                }}
              />
              <div>Loading patterns...</div>
            </div>
          ) : patterns.length ? (
            <>
              <PatternCards patterns={patterns} />

              {/* Load More Button */}
              {hasMore && (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <button
                    onClick={() => loadPatterns()}
                    disabled={isLoadingMore}
                    style={{
                      padding: '12px 32px',
                      fontSize: 16,
                      fontWeight: 700,
                      fontFamily: 'var(--font-body)',
                      color: 'var(--white)',
                      background: isLoadingMore ? 'rgba(59, 130, 135, 0.7)' : 'var(--primary-teal)',
                      border: 'none',
                      borderRadius: 9999,
                      cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 12,
                      boxShadow: 'var(--shadow-button)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoadingMore) {
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 12px 20px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-button)'
                    }}
                  >
                    {isLoadingMore ? (
                      <>
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTop: '2px solid var(--white)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        Loading...
                      </>
                    ) : (
                      'Load More Patterns'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : fetchError ? (
            <div
              style={{
                textAlign: 'center',
                padding: '64px',
                color: 'var(--danger)',
                fontSize: 16,
                background: '#fef2f2',
                borderRadius: 8,
                border: '1px solid #fecaca',
              }}
            >
              Error: {fetchError}
            </div>
          ) : hasSearched ? (
            <div
              style={{
                textAlign: 'center',
                padding: '64px',
                color: '#64748b',
                fontSize: 16,
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No results for this timeframe</div>
              <div>Try adjusting your filters or selecting a different time period</div>
            </div>
          ) : null}
        </div>
      </main>

      {/* <ShareYourSpark /> */}
    </div>
  )
}

export default Home
